import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft,
  User,
  Lock,
  Bell,
  Palette,
  Shield,
  CreditCard,
  Eye,
  EyeOff,
  Smartphone,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Building2,
  DollarSign,
  Sun,
  Moon,
  Globe,
  Download,
  Trash2,
  LogOut,
  Settings,
  ChevronRight,
  CheckCircle2,
  AlertCircle,
  Info,
  HelpCircle,
  MessageCircle,
  FileText,
  BarChart3,
  Zap,
  Star,
  Gift,
  Camera,
  Edit3,
  Save,
  X,
  Check,
  Fingerprint,
  Wifi,
  WifiOff,
  Volume2,
  VolumeX,
  Monitor,
  RefreshCw,
  Database,
  Share2,
  ExternalLink,
  Target,
  PieChart,
  TrendingUp,
  Award,
  Clock,
  History
} from 'lucide-react';

const UserSettings = ({ theme, isDarkMode, setIsDarkMode }) => {
  const [currentSection, setCurrentSection] = useState('main');
  const [editingField, setEditingField] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [autoPayEnabled, setAutoPayEnabled] = useState(false);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [smsNotifications, setSmsNotifications] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [dataSync, setDataSync] = useState(true);
  const [locationServices, setLocationServices] = useState(false);
  const [analyticsSharing, setAnalyticsSharing] = useState(true);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [language, setLanguage] = useState('en');
  const [currency, setCurrency] = useState('USD');
  const [timeFormat, setTimeFormat] = useState('12');
  const [showBalance, setShowBalance] = useState(true);

  const [userProfile, setUserProfile] = useState({
    firstName: 'Alex',
    lastName: 'Johnson',
    email: 'alex.johnson@email.com',
    phone: '+1 (555) 123-4567',
    address: '123 Main Street',
    city: 'New York',
    state: 'NY',
    zipCode: '10001',
    birthDate: '1990-05-15',
    occupation: 'Software Engineer',
    annualIncome: '85000'
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });


  const settingsSections = [
    {
      id: 'profile',
      title: 'Profile',
      icon: User,
      description: 'Personal information and account details',
      badge: null
    },
    {
      id: 'security',
      title: 'Security',
      icon: Lock,
      description: 'Password, biometrics, and authentication',
      badge: twoFactorEnabled ? 'Secure' : 'Basic'
    },
    {
      id: 'notifications',
      title: 'Notifications',
      icon: Bell,
      description: 'Alerts, reminders, and communication preferences',
      badge: notificationsEnabled ? 'On' : 'Off'
    },
    {
      id: 'appearance',
      title: 'Appearance',
      icon: Palette,
      description: 'Theme, language, and display settings',
      badge: isDarkMode ? 'Dark' : 'Light'
    },
    {
      id: 'payment',
      title: 'Payment Settings',
      icon: CreditCard,
      description: 'Payment methods and autopay preferences',
      badge: autoPayEnabled ? 'Auto' : 'Manual'
    },
    {
      id: 'privacy',
      title: 'Privacy & Data',
      icon: Shield,
      description: 'Data sharing and privacy controls',
      badge: null
    },
    {
      id: 'help',
      title: 'Help & Support',
      icon: HelpCircle,
      description: 'Documentation, contact, and feedback',
      badge: null
    }
  ];

  const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'pt', name: 'Português', flag: '🇧🇷' },
    { code: 'zh', name: '中文', flag: '🇨🇳' }
  ];

  const currencies = [
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' }
  ];

  const ToggleSwitch = ({ enabled, onToggle, size = 'md' }) => {
    const sizeClasses = {
      sm: 'w-8 h-4',
      md: 'w-10 h-5',
      lg: 'w-12 h-6'
    };
    
    const thumbSizes = {
      sm: 'w-2.5 h-2.5',
      md: 'w-3 h-3',
      lg: 'w-4 h-4'
    };

    return (
      <button
        onClick={onToggle}
        className={`${sizeClasses[size]} rounded-full relative transition-colors ${
          enabled ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <div className={`${thumbSizes[size]} bg-white rounded-full absolute top-1 transition-transform ${
          enabled ? `translate-x-${size === 'sm' ? '4' : size === 'md' ? '6' : '7'}` : 'translate-x-1'
        }`} />
      </button>
    );
  };

  const SettingItem = ({ icon: Icon, title, subtitle, action, badge, danger = false, onClick }) => (
    <button
      onClick={onClick}
      className={`w-full ${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border} text-left hover:border-blue-500 transition-all group`}
    >
      <div className="flex items-center space-x-4">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
          danger ? 'bg-red-500/20' : 'bg-blue-500/20'
        }`}>
          <Icon className={`w-5 h-5 ${danger ? theme.error : theme.accent}`} />
        </div>
        
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-1">
            <span className={`font-medium ${danger ? theme.error : theme.text}`}>{title}</span>
            {badge && (
              <span className="text-xs bg-blue-500/20 text-blue-500 px-2 py-1 rounded-full">
                {badge}
              </span>
            )}
          </div>
          {subtitle && (
            <p className={`text-xs ${theme.textMuted}`}>{subtitle}</p>
          )}
        </div>
        
        {action || <ChevronRight className={`w-4 h-4 ${theme.textMuted} group-hover:text-blue-500 transition-colors`} />}
      </div>
    </button>
  );

  const ProfileSection = () => (
    <div className="space-y-4">
      {/* Profile Header */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <div className="flex items-center space-x-4 mb-4">
          <div className="relative">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
              <span className="text-white text-xl font-bold">
                {userProfile.firstName[0]}{userProfile.lastName[0]}
              </span>
            </div>
            <button className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
              <Camera className="w-3 h-3 text-white" />
            </button>
          </div>
          
          <div className="flex-1">
            <h3 className={`text-lg font-semibold ${theme.text}`}>
              {userProfile.firstName} {userProfile.lastName}
            </h3>
            <p className={`text-sm ${theme.textMuted}`}>{userProfile.email}</p>
            <div className="flex items-center space-x-2 mt-1">
              <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              <span className="text-xs text-emerald-500">Verified Account</span>
            </div>
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-3`}>Personal Information</h4>
        
        <div className="space-y-3">
          {[
            { key: 'firstName', label: 'First Name', value: userProfile.firstName, icon: User },
            { key: 'lastName', label: 'Last Name', value: userProfile.lastName, icon: User },
            { key: 'email', label: 'Email', value: userProfile.email, icon: Mail },
            { key: 'phone', label: 'Phone', value: userProfile.phone, icon: Phone },
            { key: 'birthDate', label: 'Birth Date', value: userProfile.birthDate, icon: Calendar, type: 'date' }
          ].map((field) => (
            <div key={field.key} className="flex items-center justify-between py-2">
              <div className="flex items-center space-x-3">
                <field.icon className={`w-4 h-4 ${theme.textMuted}`} />
                <div>
                  <div className={`text-sm ${theme.text}`}>{field.label}</div>
                  <div className={`text-xs ${theme.textMuted}`}>{field.value}</div>
                </div>
              </div>
              <button
                onClick={() => setEditingField(field.key)}
                className={`text-xs ${theme.accent} font-medium`}
              >
                Edit
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Address Information */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-3`}>Address</h4>
        
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <MapPin className={`w-4 h-4 ${theme.textMuted}`} />
            <div>
              <div className={`text-sm ${theme.text}`}>{userProfile.address}</div>
              <div className={`text-xs ${theme.textMuted}`}>
                {userProfile.city}, {userProfile.state} {userProfile.zipCode}
              </div>
            </div>
          </div>
        </div>
        
        <button className={`mt-3 text-xs ${theme.accent} font-medium`}>
          Update Address
        </button>
      </div>

      {/* Financial Information */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-3`}>Financial Information</h4>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center space-x-3">
              <Building2 className={`w-4 h-4 ${theme.textMuted}`} />
              <div>
                <div className={`text-sm ${theme.text}`}>Occupation</div>
                <div className={`text-xs ${theme.textMuted}`}>{userProfile.occupation}</div>
              </div>
            </div>
            <button className={`text-xs ${theme.accent} font-medium`}>Edit</button>
          </div>
          
          <div className="flex items-center justify-between py-2">
            <div className="flex items-center space-x-3">
              <DollarSign className={`w-4 h-4 ${theme.textMuted}`} />
              <div>
                <div className={`text-sm ${theme.text}`}>Annual Income</div>
                <div className={`text-xs ${theme.textMuted}`}>
                  {showBalance ? `$${parseInt(userProfile.annualIncome).toLocaleString()}` : '••••••'}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowBalance(!showBalance)}
                className={`text-xs ${theme.textMuted}`}
              >
                {showBalance ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button className={`text-xs ${theme.accent} font-medium`}>Edit</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const SecuritySection = () => (
    <div className="space-y-4">
      {/* Security Status */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <div className="flex items-center space-x-3 mb-3">
          <Shield className="w-5 h-5 text-emerald-500" />
          <span className={`text-sm font-semibold ${theme.text}`}>Security Status</span>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className={`text-lg font-bold ${twoFactorEnabled ? theme.success : theme.warning}`}>
              {twoFactorEnabled ? 'Strong' : 'Basic'}
            </div>
            <div className={`text-xs ${theme.textMuted}`}>Security Level</div>
          </div>
          <div className="text-center">
            <div className={`text-lg font-bold ${theme.accent}`}>85%</div>
            <div className={`text-xs ${theme.textMuted}`}>Security Score</div>
          </div>
        </div>
        
        <div className="mt-3">
          <div className="flex justify-between text-xs mb-1">
            <span className={theme.textMuted}>Security Score</span>
            <span className={theme.text}>85/100</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div className="bg-emerald-500 h-2 rounded-full w-[85%]"></div>
          </div>
        </div>
      </div>

      {/* Password Settings */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-3`}>Password & Authentication</h4>
        
        <div className="space-y-4">
          <div className="space-y-3">
            <div>
              <label className={`block text-xs ${theme.textMuted} mb-2`}>Current Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                  className={`w-full px-3 py-2 pr-10 ${theme.cardBg} border ${theme.border} rounded-lg ${theme.text} focus:border-blue-500 focus:outline-none`}
                  placeholder="Enter current password"
                />
                <button
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  {showPassword ? 
                    <EyeOff className={`w-4 h-4 ${theme.textMuted}`} /> : 
                    <Eye className={`w-4 h-4 ${theme.textMuted}`} />
                  }
                </button>
              </div>
            </div>
            
            <div>
              <label className={`block text-xs ${theme.textMuted} mb-2`}>New Password</label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                className={`w-full px-3 py-2 ${theme.cardBg} border ${theme.border} rounded-lg ${theme.text} focus:border-blue-500 focus:outline-none`}
                placeholder="Enter new password"
              />
            </div>
            
            <div>
              <label className={`block text-xs ${theme.textMuted} mb-2`}>Confirm New Password</label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                className={`w-full px-3 py-2 ${theme.cardBg} border ${theme.border} rounded-lg ${theme.text} focus:border-blue-500 focus:outline-none`}
                placeholder="Confirm new password"
              />
            </div>
          </div>
          
          <button className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg font-medium transition-colors">
            Update Password
          </button>
        </div>
      </div>

      {/* Biometric Authentication */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <Fingerprint className="w-5 h-5 text-blue-500" />
            <div>
              <div className={`text-sm font-medium ${theme.text}`}>Biometric Authentication</div>
              <div className={`text-xs ${theme.textMuted}`}>Use fingerprint or face recognition</div>
            </div>
          </div>
          <ToggleSwitch enabled={biometricEnabled} onToggle={() => setBiometricEnabled(!biometricEnabled)} />
        </div>
      </div>

      {/* Two-Factor Authentication */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <Smartphone className="w-5 h-5 text-emerald-500" />
            <div>
              <div className={`text-sm font-medium ${theme.text}`}>Two-Factor Authentication</div>
              <div className={`text-xs ${theme.textMuted}`}>
                {twoFactorEnabled ? 'SMS verification enabled' : 'Add extra security layer'}
              </div>
            </div>
          </div>
          <ToggleSwitch enabled={twoFactorEnabled} onToggle={() => setTwoFactorEnabled(!twoFactorEnabled)} />
        </div>
      </div>

      {/* Login History */}
      <SettingItem
        icon={History}
        title="Login History"
        subtitle="View recent login attempts"
        onClick={() => {}}
      />

      {/* Active Sessions */}
      <SettingItem
        icon={Monitor}
        title="Active Sessions"
        subtitle="Manage logged in devices"
        badge="3 devices"
        onClick={() => {}}
      />
    </div>
  );

  const NotificationsSection = () => (
    <div className="space-y-4">
      {/* Master Notification Toggle */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Bell className="w-5 h-5 text-blue-500" />
            <div>
              <div className={`text-sm font-medium ${theme.text}`}>All Notifications</div>
              <div className={`text-xs ${theme.textMuted}`}>Master notification control</div>
            </div>
          </div>
          <ToggleSwitch enabled={notificationsEnabled} onToggle={() => setNotificationsEnabled(!notificationsEnabled)} />
        </div>
      </div>

      {/* Notification Types */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-4`}>Notification Types</h4>
        
        <div className="space-y-4">
          {[
            {
              icon: DollarSign,
              title: 'Payment Reminders',
              subtitle: 'Due dates and payment confirmations',
              enabled: true
            },
            {
              icon: TrendingUp,
              title: 'Credit Score Updates',
              subtitle: 'Changes in your credit score',
              enabled: true
            },
            {
              icon: Gift,
              title: 'Promotional Offers',
              subtitle: 'Special rates and deals',
              enabled: false
            },
            {
              icon: AlertCircle,
              title: 'Security Alerts',
              subtitle: 'Login attempts and security issues',
              enabled: true
            },
            {
              icon: FileText,
              title: 'Document Updates',
              subtitle: 'New statements and reports',
              enabled: true
            }
          ].map((item, index) => (
            <div key={index} className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <item.icon className={`w-4 h-4 ${theme.textMuted}`} />
                <div>
                  <div className={`text-sm ${theme.text}`}>{item.title}</div>
                  <div className={`text-xs ${theme.textMuted}`}>{item.subtitle}</div>
                </div>
              </div>
              <ToggleSwitch enabled={item.enabled} onToggle={() => {}} size="sm" />
            </div>
          ))}
        </div>
      </div>

      {/* Delivery Methods */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-4`}>Delivery Methods</h4>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Mail className="w-4 h-4 text-blue-500" />
              <div>
                <div className={`text-sm ${theme.text}`}>Email Notifications</div>
                <div className={`text-xs ${theme.textMuted}`}>{userProfile.email}</div>
              </div>
            </div>
            <ToggleSwitch enabled={emailNotifications} onToggle={() => setEmailNotifications(!emailNotifications)} />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <MessageCircle className="w-4 h-4 text-emerald-500" />
              <div>
                <div className={`text-sm ${theme.text}`}>SMS Notifications</div>
                <div className={`text-xs ${theme.textMuted}`}>{userProfile.phone}</div>
              </div>
            </div>
            <ToggleSwitch enabled={smsNotifications} onToggle={() => setSmsNotifications(!smsNotifications)} />
          </div>
          
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Volume2 className="w-4 h-4 text-purple-500" />
              <div>
                <div className={`text-sm ${theme.text}`}>Sound & Vibration</div>
                <div className={`text-xs ${theme.textMuted}`}>In-app audio alerts</div>
              </div>
            </div>
            <ToggleSwitch enabled={soundEnabled} onToggle={() => setSoundEnabled(!soundEnabled)} />
          </div>
        </div>
      </div>

      {/* Notification Schedule */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-3`}>Quiet Hours</h4>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className={`text-sm ${theme.text}`}>Enable Quiet Hours</span>
            <ToggleSwitch enabled={false} onToggle={() => {}} />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={`block text-xs ${theme.textMuted} mb-1`}>Start Time</label>
              <input
                type="time"
                value="22:00"
                className={`w-full px-3 py-2 ${theme.cardBg} border ${theme.border} rounded-lg ${theme.text} focus:border-blue-500 focus:outline-none`}
              />
            </div>
            <div>
              <label className={`block text-xs ${theme.textMuted} mb-1`}>End Time</label>
              <input
                type="time"
                value="07:00"
                className={`w-full px-3 py-2 ${theme.cardBg} border ${theme.border} rounded-lg ${theme.text} focus:border-blue-500 focus:outline-none`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const AppearanceSection = () => (
    <div className="space-y-4">
      {/* Theme Settings */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-4`}>Theme</h4>
        
        <div className="grid grid-cols-3 gap-3">
          {[
            { name: 'Light', icon: Sun, active: !isDarkMode },
            { name: 'Dark', icon: Moon, active: isDarkMode },
            { name: 'Auto', icon: Monitor, active: false }
          ].map((themeOption, index) => (
            <button
              key={index}
              onClick={() => index === 1 ? setIsDarkMode(true) : index === 0 ? setIsDarkMode(false) : null}
              className={`p-3 rounded-xl border transition-all ${
                themeOption.active 
                  ? 'border-blue-500 bg-blue-500/10' 
                  : `${theme.border} ${theme.cardBg}`
              }`}
            >
              <themeOption.icon className={`w-5 h-5 mx-auto mb-2 ${
                themeOption.active ? 'text-blue-500' : theme.textMuted
              }`} />
              <div className={`text-xs font-medium ${
                themeOption.active ? theme.accent : theme.text
              }`}>
                {themeOption.name}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Language Settings */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <div className="space-y-3">
          <div>
            <label className={`block text-xs ${theme.textMuted} mb-2`}>Language</label>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className={`w-full px-3 py-2 ${theme.cardBg} border ${theme.border} rounded-lg ${theme.text} focus:border-blue-500 focus:outline-none`}
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className={`block text-xs ${theme.textMuted} mb-2`}>Currency</label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className={`w-full px-3 py-2 ${theme.cardBg} border ${theme.border} rounded-lg ${theme.text} focus:border-blue-500 focus:outline-none`}
            >
              {currencies.map((curr) => (
                <option key={curr.code} value={curr.code}>
                  {curr.symbol} {curr.name}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className={`block text-xs ${theme.textMuted} mb-2`}>Time Format</label>
            <div className="grid grid-cols-2 gap-2">
              {['12', '24'].map((format) => (
                <button
                  key={format}
                  onClick={() => setTimeFormat(format)}
                  className={`p-2 rounded-lg border transition-all ${
                    timeFormat === format 
                      ? 'border-blue-500 bg-blue-500/10 text-blue-500' 
                      : `${theme.border} ${theme.cardBg} ${theme.text}`
                  }`}
                >
                  <div className="text-xs font-medium">
                    {format === '12' ? '12:00 PM' : '15:00'}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Display Settings */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-4`}>Display Settings</h4>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-sm ${theme.text}`}>Show Account Balances</div>
              <div className={`text-xs ${theme.textMuted}`}>Display amounts by default</div>
            </div>
            <ToggleSwitch enabled={showBalance} onToggle={() => setShowBalance(!showBalance)} />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-sm ${theme.text}`}>Reduce Motion</div>
              <div className={`text-xs ${theme.textMuted}`}>Minimize animations</div>
            </div>
            <ToggleSwitch enabled={false} onToggle={() => {}} />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-sm ${theme.text}`}>High Contrast</div>
              <div className={`text-xs ${theme.textMuted}`}>Improve text readability</div>
            </div>
            <ToggleSwitch enabled={false} onToggle={() => {}} />
          </div>
        </div>
      </div>

      {/* Font Size */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-4`}>Font Size</h4>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className={`text-xs ${theme.textMuted}`}>Small</span>
            <span className={`text-lg ${theme.textMuted}`}>Large</span>
          </div>
          <div className="relative">
            <input
              type="range"
              min="12"
              max="20"
              defaultValue="14"
              className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
            />
          </div>
          <div className="text-center">
            <span className={`text-sm ${theme.text}`}>Preview text size</span>
          </div>
        </div>
      </div>
    </div>
  );

  const PaymentSection = () => (
    <div className="space-y-4">
      {/* Auto-Pay Settings */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <Zap className="w-5 h-5 text-emerald-500" />
            <div>
              <div className={`text-sm font-medium ${theme.text}`}>Auto-Pay</div>
              <div className={`text-xs ${theme.textMuted}`}>Automatic loan payments</div>
            </div>
          </div>
          <ToggleSwitch enabled={autoPayEnabled} onToggle={() => setAutoPayEnabled(!autoPayEnabled)} />
        </div>
        
        {autoPayEnabled && (
          <div className="space-y-3 pt-3 border-t border-gray-200/20">
            <div>
              <label className={`block text-xs ${theme.textMuted} mb-2`}>Payment Amount</label>
              <select className={`w-full px-3 py-2 ${theme.cardBg} border ${theme.border} rounded-lg ${theme.text} focus:border-blue-500 focus:outline-none`}>
                <option>Minimum Payment</option>
                <option>Fixed Amount</option>
                <option>Percentage of Balance</option>
              </select>
            </div>
            
            <div>
              <label className={`block text-xs ${theme.textMuted} mb-2`}>Payment Date</label>
              <select className={`w-full px-3 py-2 ${theme.cardBg} border ${theme.border} rounded-lg ${theme.text} focus:border-blue-500 focus:outline-none`}>
                <option>5 days before due date</option>
                <option>3 days before due date</option>
                <option>1 day before due date</option>
                <option>On due date</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Payment Methods */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <div className="flex items-center justify-between mb-4">
          <h4 className={`text-sm font-semibold ${theme.text}`}>Payment Methods</h4>
          <button className={`text-xs ${theme.accent} font-medium`}>Add New</button>
        </div>
        
        <div className="space-y-3">
          {[
            {
              type: 'Checking Account',
              name: 'Chase Checking',
              number: '****2847',
              default: true,
              icon: Building2
            },
            {
              type: 'Credit Card',
              name: 'Visa Platinum',
              number: '****4521',
              default: false,
              icon: CreditCard
            },
            {
              type: 'Savings Account',
              name: 'High Yield Savings',
              number: '****9634',
              default: false,
              icon: PieChart
            }
          ].map((method, index) => (
            <div key={index} className={`p-3 border ${theme.border} rounded-lg flex items-center justify-between`}>
              <div className="flex items-center space-x-3">
                <method.icon className={`w-4 h-4 ${theme.textMuted}`} />
                <div>
                  <div className={`text-sm ${theme.text}`}>{method.name}</div>
                  <div className={`text-xs ${theme.textMuted}`}>{method.type} {method.number}</div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {method.default && (
                  <span className="text-xs bg-emerald-500/20 text-emerald-500 px-2 py-1 rounded-full">
                    Default
                  </span>
                )}
                <button className={`text-xs ${theme.accent}`}>Edit</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Payment Preferences */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-4`}>Payment Preferences</h4>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-sm ${theme.text}`}>Payment Confirmation</div>
              <div className={`text-xs ${theme.textMuted}`}>Email confirmation after each payment</div>
            </div>
            <ToggleSwitch enabled={true} onToggle={() => {}} />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-sm ${theme.text}`}>Round Up Payments</div>
              <div className={`text-xs ${theme.textMuted}`}>Round payments to nearest dollar</div>
            </div>
            <ToggleSwitch enabled={false} onToggle={() => {}} />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-sm ${theme.text}`}>Smart Reminders</div>
              <div className={`text-xs ${theme.textMuted}`}>AI-powered payment suggestions</div>
            </div>
            <ToggleSwitch enabled={true} onToggle={() => {}} />
          </div>
        </div>
      </div>

      {/* Cashback & Rewards */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <div className="flex items-center space-x-3 mb-3">
          <Gift className="w-5 h-5 text-purple-500" />
          <h4 className={`text-sm font-semibold ${theme.text}`}>Rewards Program</h4>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-sm ${theme.text}`}>Cashback Rewards</div>
              <div className={`text-xs ${theme.textMuted}`}>Earn 1% on all payments</div>
            </div>
            <div className="text-right">
              <div className={`text-sm font-semibold ${theme.success}`}>$24.50</div>
              <div className={`text-xs ${theme.textMuted}`}>This Year</div>
            </div>
          </div>
          
          <div className="bg-purple-500/10 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <span className={`text-xs font-medium ${theme.text}`}>Rewards Balance</span>
              <span className={`text-xs ${theme.accent}`}>Redeem</span>
            </div>
            <div className="flex items-center space-x-2">
              <Star className="w-4 h-4 text-purple-500" />
              <span className={`text-sm font-semibold ${theme.text}`}>2,450 points</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const PrivacySection = () => (
    <div className="space-y-4">
      {/* Privacy Overview */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <div className="flex items-center space-x-3 mb-3">
          <Shield className="w-5 h-5 text-emerald-500" />
          <h4 className={`text-sm font-semibold ${theme.text}`}>Privacy Overview</h4>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center">
            <div className={`text-lg font-bold ${theme.success}`}>Strong</div>
            <div className={`text-xs ${theme.textMuted}`}>Privacy Level</div>
          </div>
          <div className="text-center">
            <div className={`text-lg font-bold ${theme.accent}`}>5</div>
            <div className={`text-xs ${theme.textMuted}`}>Data Sources</div>
          </div>
        </div>
        
        <div className="mt-3">
          <div className="flex justify-between text-xs mb-1">
            <span className={theme.textMuted}>Privacy Score</span>
            <span className={theme.text}>92/100</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div className="bg-emerald-500 h-2 rounded-full w-[92%]"></div>
          </div>
        </div>
      </div>

      {/* Data Sharing Controls */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-4`}>Data Sharing</h4>
        
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-sm ${theme.text}`}>Analytics & Usage</div>
              <div className={`text-xs ${theme.textMuted}`}>Help improve our services</div>
            </div>
            <ToggleSwitch enabled={analyticsSharing} onToggle={() => setAnalyticsSharing(!analyticsSharing)} />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-sm ${theme.text}`}>Marketing Insights</div>
              <div className={`text-xs ${theme.textMuted}`}>Personalized offers and recommendations</div>
            </div>
            <ToggleSwitch enabled={false} onToggle={() => {}} />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-sm ${theme.text}`}>Credit Bureau Reporting</div>
              <div className={`text-xs ${theme.textMuted}`}>Share payment history with credit agencies</div>
            </div>
            <ToggleSwitch enabled={true} onToggle={() => {}} />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <div className={`text-sm ${theme.text}`}>Location Services</div>
              <div className={`text-xs ${theme.textMuted}`}>Find nearby branches and ATMs</div>
            </div>
            <ToggleSwitch enabled={locationServices} onToggle={() => setLocationServices(!locationServices)} />
          </div>
        </div>
      </div>

      {/* Data Management */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-4`}>Data Management</h4>
        
        <div className="space-y-3">
          <SettingItem
            icon={Download}
            title="Download My Data"
            subtitle="Export all your account information"
            onClick={() => {}}
          />
          
          <SettingItem
            icon={Database}
            title="Data Usage"
            subtitle="See what data we collect and how it's used"
            onClick={() => {}}
          />
          
          <SettingItem
            icon={RefreshCw}
            title="Data Sync"
            subtitle="Sync data across devices"
            action={<ToggleSwitch enabled={dataSync} onToggle={() => setDataSync(!dataSync)} />}
            onClick={() => {}}
          />
        </div>
      </div>

      {/* Privacy Controls */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-4`}>Privacy Controls</h4>
        
        <div className="space-y-3">
          <SettingItem
            icon={FileText}
            title="Privacy Policy"
            subtitle="Read our privacy policy"
            action={<ExternalLink className={`w-4 h-4 ${theme.textMuted}`} />}
            onClick={() => {}}
          />
          
          <SettingItem
            icon={Shield}
            title="Data Protection Rights"
            subtitle="Manage your privacy rights"
            onClick={() => {}}
          />
          
          <SettingItem
            icon={AlertCircle}
            title="Report Privacy Concern"
            subtitle="Contact our privacy team"
            onClick={() => {}}
          />
        </div>
      </div>

      {/* Account Deletion */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border} border-l-4 border-l-red-500`}>
        <h4 className={`text-sm font-semibold ${theme.error} mb-2`}>Danger Zone</h4>
        <p className={`text-xs ${theme.textMuted} mb-3`}>
          These actions are permanent and cannot be undone.
        </p>
        
        <SettingItem
          icon={Trash2}
          title="Delete Account"
          subtitle="Permanently delete your account and all data"
          danger={true}
          onClick={() => {}}
        />
      </div>
    </div>
  );

  const HelpSection = () => (
    <div className="space-y-4">
      {/* Quick Help */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-4`}>Quick Help</h4>
        
        <div className="grid grid-cols-2 gap-3">
          <button className={`p-3 ${theme.cardBg} border ${theme.border} rounded-lg hover:border-blue-500 transition-all`}>
            <MessageCircle className={`w-5 h-5 ${theme.accent} mx-auto mb-2`} />
            <div className={`text-xs font-medium ${theme.text}`}>Live Chat</div>
          </button>
          
          <button className={`p-3 ${theme.cardBg} border ${theme.border} rounded-lg hover:border-blue-500 transition-all`}>
            <Phone className={`w-5 h-5 ${theme.accent} mx-auto mb-2`} />
            <div className={`text-xs font-medium ${theme.text}`}>Call Support</div>
          </button>
          
          <button className={`p-3 ${theme.cardBg} border ${theme.border} rounded-lg hover:border-blue-500 transition-all`}>
            <Mail className={`w-5 h-5 ${theme.accent} mx-auto mb-2`} />
            <div className={`text-xs font-medium ${theme.text}`}>Email Us</div>
          </button>
          
          <button className={`p-3 ${theme.cardBg} border ${theme.border} rounded-lg hover:border-blue-500 transition-all`}>
            <HelpCircle className={`w-5 h-5 ${theme.accent} mx-auto mb-2`} />
            <div className={`text-xs font-medium ${theme.text}`}>FAQ</div>
          </button>
        </div>
      </div>

      {/* Support Resources */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-4`}>Support Resources</h4>
        
        <div className="space-y-3">
          <SettingItem
            icon={FileText}
            title="User Guide"
            subtitle="Complete guide to using LoanPro"
            onClick={() => {}}
          />
          
          <SettingItem
            icon={BarChart3}
            title="Tutorials"
            subtitle="Video tutorials and walkthroughs"
            onClick={() => {}}
          />
          
          <SettingItem
            icon={Globe}
            title="Community Forum"
            subtitle="Connect with other users"
            action={<ExternalLink className={`w-4 h-4 ${theme.textMuted}`} />}
            onClick={() => {}}
          />
          
          <SettingItem
            icon={Star}
            title="Feature Requests"
            subtitle="Suggest new features"
            onClick={() => {}}
          />
        </div>
      </div>

      {/* App Information */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-4`}>App Information</h4>
        
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className={`text-sm ${theme.textMuted}`}>Version:</span>
            <span className={`text-sm ${theme.text}`}>2.1.0</span>
          </div>
          
          <div className="flex justify-between">
            <span className={`text-sm ${theme.textMuted}`}>Last Updated:</span>
            <span className={`text-sm ${theme.text}`}>Dec 15, 2024</span>
          </div>
          
          <div className="flex justify-between">
            <span className={`text-sm ${theme.textMuted}`}>Build:</span>
            <span className={`text-sm ${theme.text}`}>2024.12.001</span>
          </div>
        </div>
        
        <div className="mt-4 pt-3 border-t border-gray-200/20">
          <button className={`text-xs ${theme.accent} font-medium`}>
            Check for Updates
          </button>
        </div>
      </div>

      {/* Legal */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-4`}>Legal</h4>
        
        <div className="space-y-3">
          <SettingItem
            icon={FileText}
            title="Terms of Service"
            subtitle="Read our terms and conditions"
            action={<ExternalLink className={`w-4 h-4 ${theme.textMuted}`} />}
            onClick={() => {}}
          />
          
          <SettingItem
            icon={Shield}
            title="Privacy Policy"
            subtitle="How we protect your privacy"
            action={<ExternalLink className={`w-4 h-4 ${theme.textMuted}`} />}
            onClick={() => {}}
          />
          
          <SettingItem
            icon={Info}
            title="Licenses"
            subtitle="Open source licenses"
            onClick={() => {}}
          />
        </div>
      </div>

      {/* Feedback */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <h4 className={`text-sm font-semibold ${theme.text} mb-3`}>Send Feedback</h4>
        <p className={`text-xs ${theme.textMuted} mb-3`}>
          Help us improve LoanPro by sharing your thoughts
        </p>
        
        <div className="grid grid-cols-2 gap-3">
          <button className={`flex items-center justify-center space-x-2 p-3 ${theme.cardBg} border ${theme.border} rounded-lg hover:border-emerald-500 transition-all`}>
            <Star className="w-4 h-4 text-emerald-500" />
            <span className={`text-xs font-medium ${theme.text}`}>Rate App</span>
          </button>
          
          <button className={`flex items-center justify-center space-x-2 p-3 ${theme.cardBg} border ${theme.border} rounded-lg hover:border-blue-500 transition-all`}>
            <Share2 className="w-4 h-4 text-blue-500" />
            <span className={`text-xs font-medium ${theme.text}`}>Share App</span>
          </button>
        </div>
      </div>
    </div>
  );

  const MainSettings = () => (
    <div className="space-y-4">
      {/* User Profile Card */}
      <div className={`${theme.cardBg} backdrop-blur-xl rounded-xl p-4 border ${theme.border}`}>
        <div className="flex items-center space-x-4">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
            <span className="text-white font-bold">
              {userProfile.firstName[0]}{userProfile.lastName[0]}
            </span>
          </div>
          
          <div className="flex-1">
            <h3 className={`font-semibold ${theme.text}`}>
              {userProfile.firstName} {userProfile.lastName}
            </h3>
            <p className={`text-sm ${theme.textMuted}`}>{userProfile.email}</p>
          </div>
          
          <button 
            onClick={() => setCurrentSection('profile')}
            className={`text-xs ${theme.accent} font-medium`}
          >
            Edit
          </button>
        </div>
      </div>

      {/* Settings Sections */}
      <div className="space-y-3">
        {settingsSections.map((section) => (
          <SettingItem
            key={section.id}
            icon={section.icon}
            title={section.title}
            subtitle={section.description}
            badge={section.badge}
            onClick={() => setCurrentSection(section.id)}
          />
        ))}
      </div>

      {/* Sign Out */}
      <div className="pt-4">
        <SettingItem
          icon={LogOut}
          title="Sign Out"
          subtitle="Sign out of your account"
          danger={true}
          onClick={() => {}}
        />
      </div>
    </div>
  );

  const renderSection = () => {
    switch(currentSection) {
      case 'profile': return <ProfileSection />;
      case 'security': return <SecuritySection />;
      case 'notifications': return <NotificationsSection />;
      case 'appearance': return <AppearanceSection />;
      case 'payment': return <PaymentSection />;
      case 'privacy': return <PrivacySection />;
      case 'help': return <HelpSection />;
      default: return <MainSettings />;
    }
  };

  return (
    <div className={`min-h-screen ${theme.bg} transition-all duration-300`}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 pt-12 pb-4 sticky backdrop-blur-xl">
        <div className="flex items-center space-x-3">
          <button 
            onClick={() => currentSection !== 'main' ? setCurrentSection('main') : window.history.back()}
            className={`w-10 h-10 ${theme.cardBg} border ${theme.border} rounded-xl flex items-center justify-center backdrop-blur-xl`}
          >
            <ArrowLeft className={`w-5 h-5 ${theme.text}`} />
          </button>
          <div>
            <h1 className={`text-xl font-bold ${theme.text}`}>
              {currentSection === 'main' ? 'Settings' : 
               settingsSections.find(s => s.id === currentSection)?.title || 'Settings'}
            </h1>
            <p className={`text-xs ${theme.textMuted}`}>
              {currentSection === 'main' ? 'Manage your account' : 
               settingsSections.find(s => s.id === currentSection)?.description || ''}
            </p>
          </div>
        </div>        
        
      </div>

      {/* Main Content */}
      <div className="px-4 pb-8 max-w-md mx-auto lg:max-w-2xl lg:px-8">
        {renderSection()}
      </div>
    </div>
  );
};

export default UserSettings;